import { v4 as uuid } from 'uuid';
import objectPath from 'object-path';
import {
  compileStatement,
  convertRulesToPolicy,
  userFacingError,
} from '../../../containers/ReturnsHeadlessConfig/PolicyBuilder/policyTranslation';
import { API_REQUEST } from '../../../lib/api';
import { RETURNS_RULES } from '../../../constants/endpoints';
import { RULE_TYPES, MODE_KEYS } from '../../../constants/returns/returnRules';
import { RULE_TITLES } from '../../../constants/copy/returns';
import {
  RECEIVE_RETURNS_RULES_PUBLISH,
  receiveRules,
  receiveRulesError,
  receiveRulesMetadata,
  setPublishingState,
} from '../../returnsRulesActions';

// A server reason longer than this is cut, so the alert stays readable.
const MAX_REASON_LENGTH = 500;

const capReason = reason =>
  reason.length > MAX_REASON_LENGTH
    ? `${reason.slice(0, MAX_REASON_LENGTH)}...`
    : reason;

// hub-backend rejects with {errors:[{message}]}; return-services with {error:{message}}.
// Any other body (e.g. a proxy's HTML page) is logged, and only the status is shown.
const readPublishError = async response => {
  const text = await response.text();
  let body;
  try {
    body = JSON.parse(text);
  } catch (e) {
    body = undefined;
  }
  const message = body?.errors?.[0]?.message || body?.error?.message;
  if (message) return capReason(String(message));
  console.error(`Policy publish failed with HTTP ${response.status}`, text);
  return `HTTP ${response.status}`;
};

const hasRestrictedSubject = (rule, restrictedSubjects) => {
  if (!restrictedSubjects || !restrictedSubjects.iris) {
    return false;
  }

  if (rule.conditions && Array.isArray(rule.conditions)) {
    return rule.conditions.some(condition =>
      restrictedSubjects.iris.includes(condition.subject),
    );
  }

  return false;
};

// Forces rules to be disabled if it uses restricted rule conditions and the feature is not on
const sanitizeRules = (rules, restrictedSubjects, irisMode) => {
  if (!rules || !Array.isArray(rules)) {
    return rules;
  }

  const irisEnabled = irisMode === 'true';

  return rules.map(rule => {
    const usesRestrictedSubject = hasRestrictedSubject(
      rule,
      restrictedSubjects,
    );
    const shouldForceDisable = usesRestrictedSubject && !irisEnabled;

    if (shouldForceDisable) {
      return {
        ...rule,
        enabled: false,
      };
    }

    return rule;
  });
};

export const v2Publish = props =>
  new Promise((resolve, reject) => {
    try {
      const {
        rules,
        listedRules = rules,
        url,
        policies,
        ruleType,
        uiSchema,
        dcs,
        warnings,
      } = props;
      const payload = policies[ruleType];

      // The priority is the rule's row in the list the user sees (listedRules).
      const statements = rules.map(rule => ({
        ...compileStatement({
          rule,
          index: listedRules.indexOf(rule),
          uiSchema,
          warnings,
          ruleType,
        }),
        id: rule.id || uuid(),
      }));

      objectPath.set(payload, 'policy_ui', rules);
      objectPath.set(payload, 'policy.statements', statements);

      fetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(async e => {
          if (e.status === 201) {
            return resolve({
              type: 'expression',
              name: ruleType,
              value: {
                rules,
                policyPayload: await e.json(),
                dcs,
              },
            });
          }
          throw userFacingError(await readPublishError(e));
        })
        .catch(err => {
          console.error(err);
          reject(err);
        });
    } catch (e) {
      console.error(e);
      reject(e);
    }
  });

const expressionEnginePublish = ({
  policyRules,
  policies,
  rules,
  dcs,
  url,
  ruleType,
  uiSchema,
  warnings,
}) =>
  new Promise((resolve, reject) => {
    if (policyRules && policies[ruleType] && policies[ruleType].policy) {
      const policy = policyRules.map(rule =>
        !rule.id
          ? {
              ...rule,
              id: uuid(),
            }
          : rule,
      );

      const policyUi = policy.map(rule => ({
        ...rule,
        actions: rule.selectedActionType
          ? rule.actions.filter(
              action => action.category === rule.selectedActionType,
            )
          : rule.actions,
        name: rule.name || 'Unnamed Rule',
      }));

      const newPolicy = convertRulesToPolicy({
        rules: policy,
        uiSchema,
        warnings,
        ruleType,
      });
      newPolicy.policy.version = policies[ruleType].policy.version;
      const payload = {
        ...newPolicy,
        policy_ui: policyUi,
        last_updated_at: policies[ruleType].last_updated_at,
      };

      fetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(async e => {
          if (e.status === 201) {
            return resolve({
              type: 'expression',
              name: ruleType,
              value: {
                rules,
                policyPayload: await e.json(),
                dcs,
              },
            });
          }
          throw userFacingError(await readPublishError(e));
        })
        .catch(err => {
          console.error(err);
          reject(err);
        });
    } else {
      reject(new Error('No policy rules found'));
    }
  });

const droolsPublish = ({ data, dispatch, apiVersion, configs = [] }) =>
  new Promise((resolve, reject) => {
    Object.keys(data).forEach(datum => {
      if (data[datum] && data[datum].criteria) {
        data[datum].criteria.forEach(criteria => {
          delete criteria.operators;
          delete criteria.values;
        });
      }
    });

    const body = JSON.stringify({
      returns: {
        rule_types: data,
        configs,
      },
    });

    dispatch({
      type: API_REQUEST,
      method: 'POST',
      body,
      path: `${RETURNS_RULES}?version=${apiVersion}`,
      name: 'fetchPublishRules',
    })
      .then(json => {
        const {
          errors,
          status_code: statusCode,
          response: {
            returns: {
              modified_by: modifiedBy,
              modified_date: modifiedDate,
            } = {},
          } = {},
        } = json;
        if (statusCode === 400) {
          throw errors[0].message;
        } else {
          resolve({
            type: 'drools',
            name: 'drools',
            value: {
              modifiedBy,
              modifiedDate,
            },
          });
        }
      })
      // A JS Error (e.g. a network failure) stays an Error, so it is not shown.
      .catch(err =>
        reject(err instanceof Error || !err?.message ? err : err.message),
      );
  });

export const fetchPublishRules = ({
  payload: { policies, rules, dcs, definitions, feePolicies = [] },
  apiVersion = 1,
  policyEnabled,
  modes = {},
  restrictedSubjects = {},
}) => dispatch => {
  dispatch(setPublishingState({ isPublishing: true }));

  const {
    [RULE_TYPES.RETURN_DESTINATION_POLICY]: returnDestinationPolicyRules = [],
    [RULE_TYPES.RETURN_METHODS_POLICY]: returnMultiCarrierPolicyRulesRaw = [],
    // eslint-disable-next-line no-unused-vars
    [RULE_TYPES.RETURN_FEES_POLICY]: returnFeesPolicyRules = [], // intentionally unused
    ...data
  } = rules;

  const returnMultiCarrierPolicyRules = sanitizeRules(
    returnMultiCarrierPolicyRulesRaw,
    restrictedSubjects,
    modes.returnsIntelligenceMode,
  );

  const sanitizedFeePolicies = sanitizeRules(
    feePolicies,
    restrictedSubjects,
    modes.returnsIntelligenceMode,
  );

  const v1FeeRules = data[RULE_TYPES.RETURN_FEES].filter(
    e => e.version !== 'v2',
  );
  objectPath.set(data, RULE_TYPES.RETURN_FEES, v1FeeRules);

  if (data?.[RULE_TYPES.RETURN_FEES]?.length === 0) {
    objectPath.del(data, RULE_TYPES.RETURN_FEES);
  }

  const sanitizedRules = {
    ...rules,
    [RULE_TYPES.RETURN_METHODS_POLICY]: returnMultiCarrierPolicyRules,
  };

  const expressionProps = {
    policies,
    definitions,
    rules: sanitizedRules,
    dcs,
    feePolicies: sanitizedFeePolicies,
  };

  // Filled per policy while it compiles; shown only for a policy that published.
  const warningsByType = {};
  const warningsFor = ruleType => {
    warningsByType[ruleType] = [];
    return warningsByType[ruleType];
  };

  const configs = [
    { key: MODE_KEYS.FEES, value: modes.feesMode || null },
    { key: MODE_KEYS.RETURN_METHODS, value: modes.returnMethodsMode || null },
    { key: MODE_KEYS.DESTINATION, value: modes.destinationMode || null },
  ];

  if (modes.hasIrisConfig) {
    configs.push({
      key: MODE_KEYS.RETURNS_INTELLIGENCE,
      value: modes.returnsIntelligenceMode || null,
    });
  }

  const publishes = [
    droolsPublish({ data, dispatch, apiVersion, configs }).catch(error => {
      console.error(error);
      // A bare string would never be found as the drools result below.
      // Show only a server reason: a string or a server body, not a JS Error.
      let reason;
      if (typeof error === 'string') {
        reason = error;
      } else if (!(error instanceof Error)) {
        reason = error?.errors?.[0]?.message;
      }
      return { type: 'drools', message: reason || 'Unknown error' };
    }),
  ];

  // Without policy access the caller sends no policies, so publish none.
  if (policyEnabled) {
    publishes.push(
      expressionEnginePublish({
        policyRules: returnMultiCarrierPolicyRules,
        ruleType: RULE_TYPES.RETURN_METHODS_POLICY,
        url: `/api/returns/policies?policy_type=${RULE_TYPES.RETURN_METHODS_POLICY}`,
        uiSchema: definitions?.[RULE_TYPES.RETURN_METHODS_POLICY]?.v2,
        warnings: warningsFor(RULE_TYPES.RETURN_METHODS_POLICY),
        ...expressionProps,
      }).catch(error => {
        console.error(error);
        return {
          type: 'expression',
          ruleType: RULE_TYPES.RETURN_METHODS_POLICY,
          name: RULE_TITLES[RULE_TYPES.RETURN_METHODS_POLICY],
          error,
        };
      }),
    );

    publishes.push(
      v2Publish({
        ...expressionProps,
        rules: sanitizedFeePolicies.filter(rule => Boolean(rule?.result)),
        listedRules: sanitizedFeePolicies,
        ruleType: RULE_TYPES.RETURN_FEES_POLICY,
        url: `/api/returns/policies?policy_type=${RULE_TYPES.RETURN_FEES_POLICY}`,
        uiSchema: definitions?.[RULE_TYPES.RETURN_FEES]?.v2,
        warnings: warningsFor(RULE_TYPES.RETURN_FEES_POLICY),
      }).catch(error => {
        console.error(error);
        return {
          type: 'expression',
          ruleType: RULE_TYPES.RETURN_FEES_POLICY,
          name: RULE_TITLES.FEE_POLICY,
          error,
        };
      }),
    );

    publishes.push(
      expressionEnginePublish({
        policyRules: returnDestinationPolicyRules,
        ruleType: RULE_TYPES.RETURN_DESTINATION_POLICY,
        url: `/api/destinations/policies`,
        uiSchema: definitions?.[RULE_TYPES.RETURN_DESTINATION_POLICY]?.v2,
        warnings: warningsFor(RULE_TYPES.RETURN_DESTINATION_POLICY),
        ...expressionProps,
      }).catch(error => {
        console.error(error);
        return {
          type: 'expression',
          ruleType: RULE_TYPES.RETURN_DESTINATION_POLICY,
          name: RULE_TITLES[RULE_TYPES.RETURN_DESTINATION_POLICY],
          error,
        };
      }),
    );
  }

  Promise.all(publishes)
    .then(items => {
      const drools = items.find(item => item.type === 'drools');
      const expressionRules = items
        .filter(item => item.type === 'expression')
        .filter(Boolean);

      const droolsError = drools && drools.message;
      const expressionErrors = [];
      const expressionSuccess = [];
      expressionRules.forEach(e => {
        if (e && e.error) {
          expressionErrors.push(e.name);
        } else {
          expressionSuccess.push(e);
        }
      });

      // A successful publish resolves with the rule type as its name.
      const warnings = expressionSuccess.flatMap(
        e => warningsByType[e.name] || [],
      );
      const withWarnings = text =>
        warnings.length > 0 ? `${text} Warning: ${warnings.join(' | ')}` : text;

      // drools fail but policy success
      if (droolsError) {
        dispatch(
          receiveRulesError(
            withWarnings(
              `A Publishing error has occurred. Please try again. ${capReason(
                String(droolsError),
              )}`,
            ),
          ),
        );
      }

      // drools success but policy fail
      if (expressionErrors.length > 0 && !droolsError) {
        // Show only server reasons and named compile errors; any other error
        // (e.g. a network failure) gets only the generic text.
        const details = expressionRules
          .filter(e => e?.error?.userFacing)
          .map(e => `${e.name}: ${capReason(e.error.message)}`)
          .join(' | ');
        dispatch(
          receiveRulesError(
            withWarnings(
              `Publishing ${expressionErrors.join(
                ' and ',
              )} has failed but other rules have published successfully. Please try again.${
                details ? ` ${details}` : ''
              }`,
            ),
          ),
        );
      }

      if (!droolsError) {
        dispatch(receiveRulesMetadata(drools.value));
      }

      const updatedPayload = expressionSuccess.reduce(
        (acc, expressionRule) => ({
          ...acc,
          policies: {
            ...acc.policies,
            [expressionRule.name]: expressionRule.value.policyPayload,
          },
        }),
        expressionProps,
      );

      dispatch(receiveRules(updatedPayload));

      // All Success
      if (!droolsError && expressionErrors.length === 0) {
        dispatch({
          type: RECEIVE_RETURNS_RULES_PUBLISH,
          warning: warnings.length > 0 ? warnings.join(' | ') : undefined,
        });
      }
      dispatch(setPublishingState({ isPublishing: false }));
    })
    .catch(err => {
      let errorMessage = 'Publishing has failed. Please try again.';
      if (typeof err === 'string') {
        errorMessage = err;
      } else {
        errorMessage = err?.errors?.[0]?.message || errorMessage;
        try {
          const parsedError = JSON.parse(err?.message || err);
          errorMessage = parsedError?.errors?.[0]?.message || errorMessage;
        } catch (e) {
          // Not a JSON string, ignore
        }
      }

      dispatch(receiveRulesError(errorMessage));
      dispatch(setPublishingState({ isPublishing: false }));
      console.error(err);
    });
};
